<template>
  <div class="location-list">
    <!-- Wynik operacji pokazuje globalny komponent powiadomień. -->
    <table class="location-table table table-striped align-middle" v-if="!loading">
      <thead class="thead-light">
        <tr>
          <th class="cell-id">{{ t('location-id') }}</th>
          <th class="cell-name">{{ t('location-name') }}</th>
          <th class="cell-short">{{ t('location-short-name') }}</th>
          <th class="cell-road">{{ t('road-number') }}</th>
          <th class="cell-direction">{{ t('direction') }}</th>
          <th class="cell-lanes">{{ t('lanes') }}</th>
          <th class="cell-actions">{{ t('action') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="location in locations" :key="location.id">
          <td class="cell-id">{{ location.id }}</td>
          <td class="cell-name" :title="location.name">{{ location.name }}</td>
          <td class="cell-short" :title="location.shortName">{{ location.shortName }}</td>
          <td class="cell-road">{{ location.roadNumber }}</td>
          <td class="cell-direction">{{ location.direction }}</td>
          <td class="cell-lanes">{{ location.lanes ? location.lanes.length : 0 }}</td>
          <td class="cell-actions">
            <!-- Przyciski zawsze w jednej linii — w stanie potwierdzania „Edytuj"
                 ustępuje miejsca parze POTWIERDŹ / ANULUJ, żeby kolumna nie
                 rozpychała wiersza na dwa poziomy. -->
            <div class="location-actions">
              <template v-if="pendingDeleteId === location.id">
                <button type="button" class="btn button-delete" @click="confirmDelete(location.id)">
                  {{ t('confirm-delete') }}
                </button>
                <button type="button" class="btn button-add-user" @click="pendingDeleteId = null">
                  {{ t('cancel') }}
                </button>
              </template>
              <template v-else>
                <button type="button" class="btn button-edit" @click="$emit('edit', location.id)">
                  {{ t('edit') }}
                </button>
                <button type="button" class="btn button-delete" @click="pendingDeleteId = location.id">
                  {{ t('delete') }}
                </button>
              </template>
            </div>
          </td>
        </tr>
        <tr v-if="!locations.length" class="location-empty">
          <td colspan="7">{{ t('locations-empty') }}</td>
        </tr>
      </tbody>
    </table>

    <div v-else class="location-loading">{{ t('loading') }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import LocationsService from "@/services/LocationsService";
import { useNotificationStore } from "@/stores/notification.store";

const { t } = useI18n();
defineEmits(["edit"]);

const locations = ref([]);
const loading = ref(false);
const pendingDeleteId = ref(null);
const notify = useNotificationStore();

async function load() {
  loading.value = true;
  try {
    const res = await LocationsService.getLocationList(1, 1000);
    // getWithCount zwraca { totalCount, content }; bez licznika — zwykłą tablicę.
    locations.value = Array.isArray(res) ? res : (res?.content ?? []);
  } catch (error) {
    notify.error(typeof error === "string" ? error : t("locations-load-error"));
    locations.value = [];
  } finally {
    loading.value = false;
  }
}

async function confirmDelete(id) {
  try {
    await LocationsService.deleteLocation(id);
    pendingDeleteId.value = null;
    notify.success(t("location-deleted"));
    await load();
  } catch (error) {
    pendingDeleteId.value = null;
    // Backend zwraca 409, gdy istnieją powiązane pomiary.
    notify.error(typeof error === "string" ? error : t("location-delete-blocked"));
  }
}

// Udostępnij reload rodzicowi (przez ref na komponencie).
defineExpose({ load });

onMounted(load);
</script>

<style scoped>
.location-list {
  background-color: var(--surface-primary);
}

/* --- tabela lokalizacji ----------------------------------------------------
   Jak w liście użytkowników: `table-layout: fixed` + szerokości na klasach
   `cell-*`, więc kolumny nie skaczą przy różnej długości nazw, a „Nazwa
   miejsca" nie zabiera całej wolnej przestrzeni. Suma = 100%. */
.location-table {
  width: 100%;
  table-layout: fixed;
}

.location-table .cell-id { width: 8%; }
.location-table .cell-name { width: 26%; }
.location-table .cell-short { width: 17%; }
.location-table .cell-road { width: 11%; }
.location-table .cell-direction { width: 10%; }
.location-table .cell-lanes { width: 11%; }
.location-table .cell-actions { width: 17%; }

/* Nagłówki mogą się zawijać (dłuższe etykiety typu „Nazwa skrócona"),
   dane już nie — nadmiar ucina wielokropek, pełna wartość jest w `title`. */
.location-table.table thead th {
  white-space: normal;
}

.location-table.table tbody td {
  padding: var(--spacing-sm) var(--spacing-md);
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Podświetlenie wiersza pod kursorem. `table-striped` maluje tło na komórkach,
   więc hover też musi celować w `td` — inaczej pasy go zasłonią. */
.location-table tbody tr:hover > td {
  background-color: var(--surface-hover);
}

/* Kolumna „Akcja": przyciski w jednej linii i w rozmiarze wierszowym.
   Domyślne 36 px wysokości z `button-system.css` łamało parę EDYTUJ/USUŃ na
   dwa poziomy i podwajało wysokość wiersza. */
.location-actions {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--spacing-xs);
}

.location-actions .btn {
  min-height: 26px;
  padding: 0 var(--spacing-sm);
  font-size: var(--font-size-2xs);
  border-radius: 4px;
  white-space: nowrap;
}

/* Stany pomocnicze — wyśrodkowane i wyciszone, bez klas z Bootstrapa. */
.location-empty td,
.location-loading {
  padding: var(--spacing-xl) var(--spacing-md);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  text-align: center;
}
</style>
